import React, { useEffect, useState, useRef } from "react";
import PaymentReceiptDropZone from "./PaymentReceiptDropZone";

const STATUS_OPTIONS = ["requested", "pending", "completed", "failed"];

function centsToDollars(cents) {
  if (typeof cents !== "number") return "-";
  return `$${(cents / 100).toFixed(2)}`;
}

const ManagePayouts = () => {
  const [payouts, setPayouts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState("requested");
  const [searchName, setSearchName] = useState("");
  const [error, setError] = useState(null);
  const [uploadingId, setUploadingId] = useState(null);
  // Handle receipt upload using S3 signed URL flow
  const handleReceiptUpload = async (e, payoutId) => {
    const file = e.target.files[0];
    if (!file) return;
    setUploadingId(payoutId);
    try {
      const token = window.localStorage.getItem('adminToken');
      const result = await uploadReceiptS3Flow(payoutId, file, token, statusFilter);
      if (result.success) {
        setPayouts(result.payouts);
      } else {
        alert(result.message || 'Upload failed');
      }
    } catch (err) {
      alert(err.message || 'Error uploading receipt');
    }
    setUploadingId(null);
  };

  // Helper to upload receipt image using S3 signed URL flow
async function uploadReceiptS3Flow(payoutId, file, token, statusFilter) {
  // 1. Get signed upload URL
  const getUrlRes = await fetch(`${process.env.REACT_APP_API_URL}/admin/generate-payout-receipt-upload-url`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`
    },
    body: JSON.stringify({
      fileName: file.name,
      fileType: file.type,
      payoutId
    })
  });
  const getUrlData = await getUrlRes.json();
  if (!getUrlData.success) throw new Error(getUrlData.message || 'Failed to get upload URL');

  // 2. Upload file to S3
  const uploadRes = await fetch(getUrlData.uploadUrl, {
    method: 'PUT',
    headers: {
      'Content-Type': file.type
    },
    body: file
  });
  if (!uploadRes.ok) throw new Error('Failed to upload to S3');

  // 3. Notify backend with receipt URL
  const notifyRes = await fetch(`${process.env.REACT_APP_API_URL}/admin/payout/upload-payout-receipt`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`
    },
    body: JSON.stringify({
      payoutId,
      receiptUrl: getUrlData.viewUrl,
      status: statusFilter
    })
  });
  return notifyRes.json();
}

const fetchPayouts = async (status) => {
    fetch(`${process.env.REACT_APP_API_URL}/admin/payouts`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${window.localStorage.getItem('adminToken')}`
      },
      body: JSON.stringify({
        status: status
      })
    })
      .then((res) => {
        if (!res.ok) throw new Error("Failed to fetch payouts");
        return res.json();
      })
      .then((data) => {
        if (data.success) {
          setPayouts(data.payouts);
        } else {
          setPayouts([]);
          setError(data.message || "No payouts found.");
        }
        setLoading(false);
      })
      .catch((err) => {
        setError(err.message);
        setPayouts([]);
        setLoading(false);
      });
};

  useEffect(() => {
    setLoading(true);
    setError(null);
    fetchPayouts(statusFilter);
  }, [statusFilter]);


  const filteredPayouts = payouts && payouts.filter((p) => {
    const nameMatch = searchName.trim() === "" || (p.guideName && p.guideName.toLowerCase().includes(searchName.trim().toLowerCase()));
    return nameMatch;
  });

  const savePayoutStatus = async (newStatus, payoutId) => {
    try {
      const token = window.localStorage.getItem('adminToken');
      const res = await fetch(`${process.env.REACT_APP_API_URL}/admin/set-payout-status`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ payoutId, status: newStatus })
      });
      fetchPayouts(statusFilter);
    } catch (err) {
      alert(err.message || 'Error updating status');
    }
  };


  return (
    <div style={{ padding: 24 }}>
      <h2>Manage Payouts</h2>
      <div style={{ display: "flex", gap: 16, marginBottom: 16 }}>
        <div>
          <label>Status: </label>
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
          >
            {STATUS_OPTIONS.map((status) => (
              <option key={status} value={status}>{status}</option>
            ))}
          </select>
        </div>
        <div>
          <label>Search by Guide Name: </label>
          <input
            type="text"
            value={searchName}
            onChange={(e) => setSearchName(e.target.value)}
            placeholder="Enter guide name..."
            style={{ padding: 4 }}
          />
        </div>
      </div>
      {loading ? (
        <div>Loading payouts...</div>
      ) : error ? (
        <div style={{ color: "red" }}>{error}</div>
      ) : filteredPayouts.length === 0 ? (
        <div>No payouts found.</div>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>
          {filteredPayouts.map((payout) => (
            <div
              key={payout._id}
              style={{
                display: "flex",
                flexWrap: "wrap",
                gap: 24,
                border: "1px solid #ddd",
                borderRadius: 8,
                padding: 16,
                background: "#fff",
                boxShadow: "0 2px 8px rgba(0,0,0,0.04)",
                alignItems: "center"
              }}
            >
              <h2
                style={{
                    color: payout.status === "completed" ? "green" : payout.status === "failed" ? "red" : "orange",
                }}
              >{payout?.status?.toUpperCase()}</h2>
              <div><strong>Guide:</strong> {payout.guideName}</div>
              <div><strong>Email:</strong> {payout.guideEmail}</div>
              <div><strong>Phone:</strong> {payout.guidePhoneNumber} ({payout.guidePhoneType})</div>
              <div><strong>Amount:</strong> {centsToDollars(payout.amount)}</div>
              <div><strong>Total Earnings:</strong> {centsToDollars(payout.totalEarnings)}</div>
              <div><strong>Cashed Out:</strong> {centsToDollars(payout.cashedOut)}</div>
              <div><strong>Currency:</strong> {payout.currency}</div>
              <div><strong>Method:</strong> {payout.payoutMethod}</div>
              <div><strong>Date:</strong> {payout.payoutDateReadable}</div>
              <div>
                <strong>Receipt:</strong> {payout.payoutImageReceipt ? (
                  <>
                    <a href={payout.payoutImageReceipt} target="_blank" rel="noopener noreferrer">View</a>
                    <button
                      onClick={() => { 
                        setPayouts(prev => prev.map(p => p._id === payout._id ? { ...p, payoutImageReceipt: null } : p));
                      }}
                      style={{
                        marginLeft: 12,
                        marginTop: 8,
                        padding: '4px 8px',
                        color: '#333',
                        border: 'none',
                        borderRadius: 4,
                        cursor: 'pointer'
                      }}
                    >
                      Edit Receipt
                    </button>
                    <div>
                      <select
                        value={statusFilter}
                        onChange={(e) => savePayoutStatus(e.target.value, payout._id)}
                        style={{ marginTop: 8 }}
                      >
                        {STATUS_OPTIONS.map((status) => (
                          <option key={status} value={status}>{status}</option>
                        ))}
                      </select>
                    </div>
                  </>
                ) : (
                  <PaymentReceiptDropZone
                    onFileSelected={file => handleReceiptUpload({ target: { files: [file] } }, payout._id)}
                    disabled={uploadingId === payout._id}
                    uploading={uploadingId === payout._id}
                  />
                )}
              </div>            
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default ManagePayouts;
